/**
 * Pathways v3, practitioner side: every call of contract §5.1 and of its phase-3
 * additions (§10.3 review queue, §10.4 attendance, §10.5 course quizzes), plus the two
 * catalogue reads the step editor picks from (`GET /booking/services`, `GET
 * /learn/courses`, §10.11) (docs/pathways-module/pathways-api-contract.md). The learner,
 * public and checkout calls are the learner lane's (pathways/learnerApi.js).
 *
 * Paths are relative to the api baseURL ("/api"), like every other caller of `api`. Ids
 * and e-mails travel through `encodeURIComponent`: an e-mail can hold "+" or "/", and the
 * server normalizes the decoded value before it compares it.
 *
 * SHAPE-GUARDED. The production proxy answers 200 + index.html for an unknown `/api` path,
 * so a 200 whose body is not the shape the contract promises is refused with
 * `payloadShapeError` instead of being rendered as "no pathways" or as an empty
 * dashboard. An HTTP error is re-thrown untouched, so callers can read its status and its
 * `detail.code` (pathways/errors.js).
 *
 * Bodies are sent exactly as the contract names them. Every write model is extra="forbid"
 * on the server, so nothing is added here that the caller did not pass.
 *
 * "@/lib/api", not a relative path: axios cannot load under Jest, and every suite that
 * mounts a pathway surface stubs this exact specifier.
 */
import { api } from "@/lib/api";
// Relative: a pure module, resolvable under Jest without a shim.
import { payloadShapeError } from "../hooks/useAdminLoad";

const enc = encodeURIComponent;

// ── shape guards ─────────────────────────────────────────────────────────────

const isObject = (v) => Boolean(v) && typeof v === "object" && !Array.isArray(v);
const isStr = (v) => typeof v === "string";
const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const isBool = (v) => typeof v === "boolean";
const isNumOrNull = (v) => v === null || isNum(v);
const isArrayOf = (v, pred) => Array.isArray(v) && v.every(pred);
const hasIdTitle = (v) => isObject(v) && isStr(v.id) && isStr(v.title);

const isProblem = (v) => isObject(v) && isStr(v.code);
const isStep = (v) => hasIdTitle(v) && isStr(v.kind) && isBool(v.required);
const isLevel = (v) => hasIdTitle(v) && isArrayOf(v.steps, isStep);

const isAdminPathway = (v) => hasIdTitle(v) && isStr(v.status) && isStr(v.path_type)
  && isArrayOf(v.levels, isLevel) && Array.isArray(v.thread) && Array.isArray(v.objectives)
  && isNum(v.learners_count) && isBool(v.path_type_locked)
  && isArrayOf(v.activation_problems, isProblem);

const isListItem = (v) => hasIdTitle(v) && isStr(v.status) && isStr(v.path_type)
  && isNum(v.learners_count) && isNumOrNull(v.capacity) && isNumOrNull(v.avg_progress);

const isFunnelBar = (v) => isObject(v) && isStr(v.key) && isStr(v.title) && isNum(v.count);
const isDashboardLearner = (v) => isObject(v) && isStr(v.email) && isNum(v.percent)
  && isBool(v.stuck) && (v.current_step === null || hasIdTitle(v.current_step))
  && isNumOrNull(v.quiz_average);
const isTotals = (v) => isObject(v) && isNum(v.enrolled) && isNum(v.active_this_week)
  && isNum(v.completed) && isNumOrNull(v.average_progress) && isNum(v.stuck);
const isLiveSession = (v) => isObject(v) && isStr(v.step_id) && isStr(v.title)
  && (v.starts_at === null || isStr(v.starts_at)) && isArrayOf(v.attended, isStr);
const isDashboard = (v) => isObject(v) && isStr(v.pathway_id) && isStr(v.title)
  && isTotals(v.totals) && isArrayOf(v.funnel, isFunnelBar)
  && isArrayOf(v.learners, isDashboardLearner)
  && isNum(v.review_pending) && isArrayOf(v.live_sessions, isLiveSession);

const isContactStep = (v) => hasIdTitle(v) && isStr(v.kind) && isStr(v.state)
  && (v.quiz === null || (isObject(v.quiz) && isNum(v.quiz.attempts)))
  && (v.submission === null || (isObject(v.submission) && isStr(v.submission.status)))
  && isBool(v.attended);
const isContactPathway = (v) => isObject(v) && isStr(v.pathway_id) && isStr(v.title)
  && isNum(v.percent) && isArrayOf(v.levels, (l) => hasIdTitle(l) && isArrayOf(l.steps, isContactStep));

const isEncourageResult = (v) => isObject(v) && isNum(v.sent) && isNum(v.skipped);

const isReviewItem = (v) => isObject(v) && isStr(v.id) && isStr(v.step_id) && isStr(v.email)
  && isStr(v.status) && isStr(v.text) && isStr(v.submitted_at)
  && (v.photo === null || (isObject(v.photo) && isStr(v.photo.url)));
const isAttendanceResult = (v) => isObject(v) && isNum(v.updated) && isNum(v.completed);
const isCourseQuiz = (v) => isObject(v) && isStr(v.course_id) && isStr(v.lesson_id) && isStr(v.title)
  && isNum(v.skipped_count)
  && isArrayOf(v.questions, (q) => isObject(q) && isStr(q.question) && isArrayOf(q.choices, isStr) && isNum(q.correct_index));
// The two catalogues are other modules' rows; only what the step editor reads is required.
const isBookingService = (v) => isObject(v) && isStr(v.id) && isStr(v.name) && isBool(v.active);
const isCourse = (v) => hasIdTitle(v) && Array.isArray(v.lessons);

/** Returns `data` when `ok(data)`, otherwise throws the shape refusal. */
const guard = (data, ok) => {
  if (!ok(data)) throw payloadShapeError();
  return data;
};

// ── calls (§5.1) ─────────────────────────────────────────────────────────────

const ADMIN = "/pathways";
const pathwayPath = (id) => `${ADMIN}/${enc(id)}`;

/** `GET /pathways` -> PathwayListItem[] (updated_at desc). */
export async function listPathways() {
  const { data } = await api.get(ADMIN);
  return guard(data, (d) => isArrayOf(d, isListItem));
}

/** `GET /pathways/{id}` -> AdminPathway. */
export async function getPathway(id) {
  const { data } = await api.get(pathwayPath(id));
  return guard(data, isAdminPathway);
}

/** `POST /pathways` with a PathwayIn -> AdminPathway (a draft). */
export async function createPathway(body) {
  const { data } = await api.post(ADMIN, body);
  return guard(data, isAdminPathway);
}

/** `PUT /pathways/{id}` with a FULL PathwayIn replacement -> AdminPathway. */
export async function updatePathway(id, body) {
  const { data } = await api.put(pathwayPath(id), body);
  return guard(data, isAdminPathway);
}

/** `POST /pathways/{id}/activate` -> AdminPathway (published); 422 PATHWAY_NOT_ACTIVATABLE + problems. */
export async function activatePathway(id) {
  const { data } = await api.post(`${pathwayPath(id)}/activate`);
  return guard(data, isAdminPathway);
}

/** `POST /pathways/{id}/archive` -> AdminPathway (archived). There is no un-archive. */
export async function archivePathway(id) {
  const { data } = await api.post(`${pathwayPath(id)}/archive`);
  return guard(data, isAdminPathway);
}

/**
 * `POST /pathways/{id}/duplicate?lang=` -> AdminPathway (the NEW draft, with new ids). The
 * title's " (copy)" suffix is worded in `lang`, the practitioner's UI language.
 */
export async function duplicatePathway(id, { lang } = {}) {
  const { data } = await api.post(`${pathwayPath(id)}/duplicate`, null, { params: { lang } });
  return guard(data, isAdminPathway);
}

/** `GET /pathways/{id}/dashboard` -> PathwayDashboard. */
export async function fetchPathwayDashboard(id) {
  const { data } = await api.get(`${pathwayPath(id)}/dashboard`);
  return guard(data, isDashboard);
}

/**
 * `GET /pathways/{id}/export.csv?lang=` -> the CSV as a Blob, fetched with the session
 * (a bare `<a href>` would carry neither the auth header nor the tenant header).
 *
 * @param {string} id
 * @param {"fr"|"en"|"es"} lang  the headers' language
 * @returns {Promise<{ blob: Blob, filename: string|null }>}  `filename` from Content-Disposition
 */
export async function downloadPathwayExport(id, lang) {
  const res = await api.get(`${pathwayPath(id)}/export.csv`, { params: { lang }, responseType: "blob" });
  const blob = res.data;
  if (!(blob instanceof Blob)) throw payloadShapeError();
  // The proxy trap again: an unknown path answers 200 + index.html, which is not a CSV.
  const type = String(blob.type || res.headers?.["content-type"] || "");
  if (type && !type.includes("text/csv")) throw payloadShapeError();
  return { blob, filename: dispositionFilename(res.headers?.["content-disposition"]) };
}

/** The filename of an `attachment; filename="x.csv"` header, or null. */
export function dispositionFilename(header) {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(String(header || ""));
  if (!m) return null;
  try {
    return decodeURIComponent(m[1].trim());
  } catch {
    return m[1].trim();
  }
}

/**
 * `POST /pathways/{id}/encourage` -> `{ sent, skipped }`.
 *
 * @param {string} id
 * @param {{ emails: string[], message: string }} body  1..500 e-mails, 1..2000 characters
 */
export async function sendEncouragement(id, { emails, message }) {
  const { data } = await api.post(`${pathwayPath(id)}/encourage`, { emails, message });
  return guard(data, isEncourageResult);
}

/**
 * `POST /pathways/{id}/prompts` -> AdminPathway (its `thread` holds the new prompt).
 * 409 PROMPTS_COHORT_ONLY on a self-paced pathway.
 *
 * @param {string} id
 * @param {{ text: string }} body
 */
export async function postPrompt(id, { text }) {
  const { data } = await api.post(`${pathwayPath(id)}/prompts`, { text });
  return guard(data, isAdminPathway);
}

/** `GET /pathways/learners/{email}` -> ContactPathway[] (enrolled_at desc): the CRM tab. */
export async function fetchContactPathways(email) {
  const { data } = await api.get(`${ADMIN}/learners/${enc(email)}`);
  return guard(data, (d) => isArrayOf(d, isContactPathway));
}

// ── phase 3 (§10) ────────────────────────────────────────────────────────────

/**
 * `GET /pathways/{id}/submissions?status=` -> ReviewItem[] (contract §10.3): `pending`
 * oldest first (the review queue), `all` newest first.
 *
 * @param {string} id
 * @param {"pending"|"all"} status
 */
export async function listSubmissions(id, status = "pending") {
  const { data } = await api.get(`${pathwayPath(id)}/submissions`, { params: { status } });
  return guard(data, (d) => isArrayOf(d, isReviewItem));
}

/** `POST /pathways/{id}/submissions/{sub_id}/approve` -> ReviewItem; 409 SUBMISSION_ALREADY_REVIEWED. */
export async function approveSubmission(id, submissionId) {
  const { data } = await api.post(`${pathwayPath(id)}/submissions/${enc(submissionId)}/approve`, {});
  return guard(data, isReviewItem);
}

/**
 * `POST /pathways/{id}/submissions/{sub_id}/feedback` -> ReviewItem. The learner is
 * e-mailed the message; the step stays done.
 *
 * @param {string} id
 * @param {string} submissionId
 * @param {{ message: string }} body  1..2000 characters, trimmed
 */
export async function sendSubmissionFeedback(id, submissionId, { message }) {
  const { data } = await api.post(`${pathwayPath(id)}/submissions/${enc(submissionId)}/feedback`, { message });
  return guard(data, isReviewItem);
}

/**
 * `POST /pathways/{id}/steps/{sid}/attendance` -> `{ updated, completed }` (contract
 * §10.4): ticking completes the step; unticking never un-completes it.
 *
 * @param {string} id
 * @param {string} stepId  a live step of a cohort pathway
 * @param {{ emails: string[], attended: boolean }} body  1..500 e-mails
 */
export async function recordAttendance(id, stepId, { emails, attended }) {
  const { data } = await api.post(`${pathwayPath(id)}/steps/${enc(stepId)}/attendance`, { emails, attended });
  return guard(data, isAttendanceResult);
}

/** `GET /pathways/course-quizzes` -> CourseQuiz[] (contract §10.5): the quiz import source. */
export async function listCourseQuizzes() {
  const { data } = await api.get(`${ADMIN}/course-quizzes`);
  return guard(data, (d) => isArrayOf(d, isCourseQuiz));
}

/** `GET /booking/services` -> the practice's session types (the self-paced live step's picker). */
export async function listBookingServices() {
  const { data } = await api.get("/booking/services");
  return guard(data, (d) => isArrayOf(d, isBookingService));
}

/** `GET /learn/courses` -> the practice's courses, every status (the course step's picker filters). */
export async function listCourses() {
  const { data } = await api.get("/learn/courses");
  return guard(data, (d) => isArrayOf(d, isCourse));
}
